import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, AlertTriangle, BadgeCheck, ImageDown } from "lucide-react";
import { api, setPageMeta, setJsonLd, clubListItem } from "@/lib/api";
import { Reveal, MaskedLines } from "@/components/Motion";
import { SectionVideo } from "@/components/SectionVideo";
import { ChecklistMagnet } from "@/components/ChecklistMagnet";
import { RankBadge } from "@/components/RankBadge";
import { toast } from "sonner";

// Audit NRC-041: optional post-quiz email capture
const QuizEmailCapture = ({ picks }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      await api.post("/quiz/email", {
        email,
        picks: picks.slice(0, 3).map((r) => ({
          name: r.club.name,
          slug: r.club.slug,
          city: r.club.city ? `${r.club.city}, ${r.club.state}` : null,
          meets: r.club.guide?.meets || null,
          score: r.club.guide?.score || null,
        })),
      });
      setSent(true);
    } catch {
      toast.error("Could not send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <p data-testid="quiz-email-sent" className="font-mono-label text-white/60 mt-6 border border-white/20 p-4">
        SENT. YOUR MATCHES + FIRST-VISIT CHECKLIST ARE IN YOUR INBOX.
      </p>
    );
  }
  return (
    <form onSubmit={submit} data-testid="quiz-email-capture" className="mt-6 border border-white/20 p-5 max-w-xl">
      <p className="font-mono-label text-white/50">WANT THESE EMAILED? (OPTIONAL)</p>
      <p className="text-white/50 text-sm mt-1.5">Your matches, their meeting times, and the first-visit checklist — one email, no spam.</p>
      <div className="mt-3 flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          data-testid="quiz-email-input"
          className="flex-1 min-w-0 bg-transparent border border-white/25 px-3 py-2.5 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200"
        />
        <button type="submit" disabled={busy} data-testid="quiz-email-submit" className="bg-white text-ink font-mono-label px-4 py-2.5 hover:bg-white/80 transition-colors disabled:opacity-60 whitespace-nowrap">
          {busy ? "SENDING…" : "EMAIL MY MATCHES"}
        </button>
      </div>
    </form>
  );
};

const RUBRIC = [
  [25, "Pace Honesty", "Do they publish real min/mile ranges? Is there a group for 11:00+/mi runners and walkers?"],
  [20, "No-Drop Mechanics", "Is there a sweeper at the back? Does the route format guarantee everyone finishes together?"],
  [15, "First-Timer Experience", "Is there an actual process for a stranger who shows up alone? Greeting, intros, pace-group placement."],
  [10, "Reliability & Comms", "Same time, same place, every week? Is the schedule published and current?"],
  [10, "Trail Etiquette", "Do they run 2-3 abreast and yield on shared paths, or swarm them?"],
  [10, "Culture & Belonging", "Do newcomers get spoken to on visit one? Is the room broader than one demographic?"],
  [5, "Access & Cost", "Free or transparently priced? Is there a non-alcoholic path to the social side?"],
  [5, "Substance", "Is it a run club with a social side, or a social club with a run attached?"],
];

const QUIZ_CITIES = ["Atlanta", "Austin", "Boston", "Charlotte", "Chicago", "Dallas", "Denver", "Detroit", "Houston", "Los Angeles", "Miami", "Nashville", "New York", "Philadelphia", "Phoenix", "Portland", "San Diego", "San Francisco", "Seattle", "Tampa", "Venice", "Washington", "West Palm Beach"];

const LEVELS = [["new", "Barely any. I need a walk option."], ["some", "I can do 3 miles without dying."], ["lots", "I'm training for something."]];
const WHYS = [["people", "To meet people"], ["train", "To get faster"], ["habit", "To build the habit"]];
const WHENS = [["morning", "Mornings"], ["evening", "Evenings"], ["weekend", "Weekends"], ["any", "Whenever"]];

const WHY_TEXT = {
  new: "clubs with a walk or short option and a real no-drop policy",
  some: "clubs that welcome a steady three-miler without dropping them",
  lots: "clubs with genuine training substance, not just a social lap",
};

function rankPool(pool, answers) {
  return pool
    .map((c) => {
      const g = c.guide;
      const [pace = 0, nodrop = 0, first = 0, rel = 0, , cult = 0, , subst = 0] = g.sub || [];
      const tags = g.qtags || [];
      let v = g.score;
      if (answers.level === "new") {
        v += nodrop * 1.5 + first * 1.2 + pace * 0.6;
        if (!tags.includes("beginner")) v -= 40;
      } else if (answers.level === "some") {
        v += nodrop * 0.7 + cult * 0.8 + first * 0.5;
      } else {
        v += subst * 3 + pace * 0.8 + rel * 0.8;
      }
      if (answers.why === "people") v += cult * 1.6 + first * 1.1;
      else if (answers.why === "train") v += subst * 2.4 + pace * 1.0;
      else v += rel * 2.2 + nodrop * 0.9;
      if (answers.when !== "any" && !tags.includes(answers.when)) v -= 22;
      return { club: c, v };
    })
    .sort((a, b) => b.v - a.v);
}

const Quiz = ({ clubs }) => {
  const [answers, setAnswers] = useState({ city: "", level: "", why: "", when: "" });
  const done = answers.city && answers.level && answers.why && answers.when;

  const result = useMemo(() => {
    if (!done) return null;
    const cityUsed = answers.city !== "__any";
    const inCity = cityUsed ? clubs.filter((c) => c.guide.qcity === answers.city) : [];
    const local = rankPool(inCity, answers).slice(0, 3);
    const need = 3 - local.length;
    const fill = need > 0 ? rankPool(clubs.filter((c) => !inCity.includes(c)), answers).slice(0, need) : [];
    if (!cityUsed) return { head: "Your top three", sub: `Ranked nationwide for ${WHY_TEXT[answers.level]}.`, picks: rankPool(clubs, answers).slice(0, 3), fill: [] };
    if (local.length === 0) return { head: `We don't cover ${answers.city} yet`, sub: `Here are the three strongest clubs in the country instead. Submit a ${answers.city} club and it goes to the top of the review list.`, picks: [], fill };
    return {
      head: `Your ${local.length === 1 ? "top pick" : local.length === 2 ? "two picks" : "top three"} in ${answers.city}`,
      sub: `Ranked for ${WHY_TEXT[answers.level]}.${need > 0 ? ` ${answers.city} has ${local.length} ${local.length === 1 ? "club" : "clubs"} in the guide, so we added the best of the rest below.` : ""}`,
      picks: local, fill,
    };
  }, [done, answers, clubs]);

  const opt = (key, value, label) => (
    <button
      key={value}
      data-testid={`quiz-${key}-${value}`}
      onClick={() => setAnswers({ ...answers, [key]: value })}
      className={`font-mono-label px-4 py-2.5 border transition-colors duration-200 ${answers[key] === value ? "bg-white text-ink border-white" : "border-white/30 text-white/70 hover:border-white"}`}
    >
      {label}
    </button>
  );

  const card = (r, i, tail) => (
    <Link key={r.club.slug} to={`/clubs/${r.club.slug}`} data-testid={`quiz-result-${i}`} className="group flex items-center gap-5 border border-white/15 p-5 hover:bg-white hover:text-ink transition-colors duration-300">
      <span className="font-display text-3xl opacity-40">{i + 1}</span>
      <span className="flex-1">
        <span className="font-display text-xl block">
          {r.club.name}
          {tail && <em className="font-mono-label not-italic ml-2 opacity-50">· ELSEWHERE</em>}
        </span>
        <span className="font-mono-label opacity-50">
          {r.club.city}, {r.club.state} {r.club.guide.meets ? `· ${r.club.guide.meets}` : ""}
        </span>
      </span>
      <span className="font-mono-label bg-white text-ink group-hover:bg-ink group-hover:text-white px-2.5 py-1.5 transition-colors duration-300">{r.club.guide.score}</span>
      <ArrowUpRight size={18} className="opacity-40 group-hover:opacity-100" />
    </Link>
  );

  return (
    <section data-testid="ranked-quiz" className="bg-ink border-b border-white/10 px-5 lg:px-10 py-20">
      <Reveal>
        <p className="font-mono-label text-white/40">60 SECONDS, NO EMAIL REQUIRED</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4">FIND YOURS.</h2>
      </Reveal>
      <div className="mt-10 space-y-8 max-w-4xl">
        <div>
          <p className="font-mono-label text-white/50 mb-3"><span className="text-white/30">1 / 4</span>&nbsp;&nbsp;WHERE ARE YOU?</p>
          <select
            data-testid="quiz-city-select"
            value={answers.city}
            onChange={(e) => setAnswers({ ...answers, city: e.target.value })}
            className="bg-night border border-white/25 text-white font-mono-label px-4 py-3.5 w-full sm:w-96 focus:border-white transition-colors duration-200"
          >
            <option value="">CHOOSE YOUR CITY</option>
            {QUIZ_CITIES.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
            <option value="__any">ANYWHERE — JUST SHOW ME THE BEST</option>
          </select>
        </div>
        <div>
          <p className="font-mono-label text-white/50 mb-3"><span className="text-white/30">2 / 4</span>&nbsp;&nbsp;HOW MUCH RUNNING HAVE YOU ACTUALLY BEEN DOING?</p>
          <div className="flex flex-wrap gap-2">{LEVELS.map(([v, l]) => opt("level", v, l))}</div>
        </div>
        <div>
          <p className="font-mono-label text-white/50 mb-3"><span className="text-white/30">3 / 4</span>&nbsp;&nbsp;WHAT'S THE REAL REASON YOU'RE GOING?</p>
          <div className="flex flex-wrap gap-2">{WHYS.map(([v, l]) => opt("why", v, l))}</div>
        </div>
        <div>
          <p className="font-mono-label text-white/50 mb-3"><span className="text-white/30">4 / 4</span>&nbsp;&nbsp;WHEN CAN YOU REALISTICALLY SHOW UP?</p>
          <div className="flex flex-wrap gap-2">{WHENS.map(([v, l]) => opt("when", v, l))}</div>
        </div>
        {result && (
          <div data-testid="quiz-results" className="border-t border-white/20 pt-8">
            <h3 className="font-display text-2xl lg:text-3xl">{result.head.toUpperCase()}</h3>
            <p className="text-white/50 text-sm mt-2 max-w-xl">{result.sub}</p>
            <div className="grid gap-3 mt-6">
              {result.picks.map((r, i) => card(r, i, false))}
              {result.fill.map((r, i) => card(r, result.picks.length + i, true))}
            </div>
            <p className="font-mono-label text-white/30 mt-5">SCORED ON PACE HONESTY, NO-DROP MECHANICS AND FIRST-TIMER EXPERIENCE.</p>
            <QuizEmailCapture picks={[...result.picks, ...result.fill]} />
          </div>
        )}
      </div>
    </section>
  );
};

export default function Ranked() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [sort, setSort] = useState("score");
  const [cityFilter, setCityFilter] = useState("");
  const [badgeClub, setBadgeClub] = useState(null);

  useEffect(() => {
    setPageMeta("The Best Run Clubs in America, Ranked (2026) | Ninja Run Club", "31 run clubs across 23 cities, scored out of 100 on pace honesty, no-drop mechanics, and first-timer experience. Plus a 60-second quiz to find yours.");
    api.get("/clubs/ranked").then((r) => setData(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!data) return;
    const sorted = [...data.items].sort((a, b) => b.guide.score - a.guide.score);
    return setJsonLd("ranked-jsonld", {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "The Best Run Clubs in America, Ranked (2026)",
      description: "Run clubs scored out of 100 on pace honesty, no-drop mechanics, first-timer experience, reliability, etiquette, culture, access, and substance.",
      url: "https://ninjarunclub.com/ranked",
      itemListOrder: "https://schema.org/ItemListOrderDescending",
      numberOfItems: sorted.length,
      itemListElement: sorted.map((c, i) => clubListItem(c, i + 1)),
    });
  }, [data]);

  const items = useMemo(() => {
    if (!data) return [];
    let list = [...data.items];
    if (cityFilter) list = list.filter((c) => c.guide.qcity === cityFilter);
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "city") list.sort((a, b) => (a.city || "").localeCompare(b.city || ""));
    else list.sort((a, b) => b.guide.score - a.guide.score);
    return list;
  }, [data, sort, cityFilter]);

  if (!data) return <p className="font-mono-label text-white/40 py-32 text-center">LOADING THE LIST...</p>;

  const rankOf = (club) => data.items.findIndex((c) => c.slug === club.slug) + 1;
  const lastVerified = (() => {
    const max = data.items.reduce((m, c) => ((c.last_verified || "") > m ? c.last_verified : m), "");
    if (!max) return null;
    return new Date(`${max}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }).toUpperCase();
  })();

  return (
    <div className="bg-night min-h-screen">
      <header className="relative overflow-hidden px-5 lg:px-10 pt-20 pb-14 border-b border-white/10 bg-ink">
        <SectionVideo src="/video/run-14.mp4" srcMobile="/video/run-14-mobile.mp4" poster="/video/run-14-poster.jpg" opacity={0.22} testid="ranked-hero-video" />
        <div className="relative">
        <p className="font-mono-label text-white/50">
          THE 2026 LIST · {data.items.length} RANKED CLUBS · 23 CITIES
          {stats && <span data-testid="ranked-directory-count"> · FROM A DIRECTORY OF {stats.clubs} CLUBS</span>}
          {lastVerified && <span data-testid="ranked-last-verified"> · LAST VERIFIED {lastVerified}</span>}
        </p>
        <MaskedLines
          as="h1"
          lines={["THE BEST RUN CLUBS", "IN AMERICA. RANKED."]}
          lineClassName="font-display text-3xl sm:text-5xl lg:text-6xl leading-[0.98]"
          className="mt-5"
        />
        <p className="text-white/60 text-sm max-w-2xl mt-6">
          Every club scored out of 100 on the things runners actually complain about: pace honesty, no-drop mechanics, and what happens when a stranger shows up alone. No pay-to-play, no aesthetics points.
        </p>
        </div>
      </header>

      <section className="px-5 lg:px-10 py-10 border-b border-white/10">
        <ChecklistMagnet source="checklist-ranked" />
      </section>

      <Quiz clubs={data.items} />

      {badgeClub && (
        <RankBadge club={badgeClub} rank={rankOf(badgeClub)} total={data.items.length} onClose={() => setBadgeClub(null)} />
      )}

      <div data-testid="ranked-controls" className="sticky top-16 z-40 bg-night/90 backdrop-blur-xl border-b border-white/10 px-5 lg:px-10 py-4 flex flex-wrap items-center gap-3">
        <span className="font-mono-label text-white/40">SORT</span>
        {[["score", "SCORE"], ["name", "NAME"], ["city", "CITY"]].map(([v, l]) => (
          <button key={v} data-testid={`ranked-sort-${v}`} onClick={() => setSort(v)}
            className={`font-mono-label px-4 py-2.5 border transition-colors duration-200 ${sort === v ? "bg-white text-ink border-white" : "border-white/20 text-white/60 hover:border-white"}`}>
            {l}
          </button>
        ))}
        <select data-testid="ranked-city-filter" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}
          className="ml-auto bg-transparent border border-white/20 text-white font-mono-label px-4 py-2.5 focus:border-white transition-colors duration-200">
          <option value="" className="bg-night">ALL CITIES</option>
          {QUIZ_CITIES.map((c) => <option key={c} value={c} className="bg-night">{c.toUpperCase()}</option>)}
        </select>
      </div>

      <section className="px-5 lg:px-10 py-8" data-testid="ranked-leaderboard">
        {items.map((c) => (
          <div key={c.slug} className="relative">
          <Link
            to={`/clubs/${c.slug}`}
            data-testid={`ranked-row-${c.slug}`}
            className="group grid grid-cols-12 items-center gap-3 border-b border-white/10 py-5 px-2 hover:bg-white hover:text-ink transition-colors duration-300"
          >
            <span className="col-span-2 md:col-span-1 font-display text-2xl md:text-3xl opacity-40">{String(rankOf(c)).padStart(2, "0")}</span>
            <div className="col-span-10 md:col-span-4">
              <h3 className="font-display text-xl md:text-2xl leading-tight flex items-center gap-2">
                {c.name}
                {c.guide.warning && <AlertTriangle size={14} className="opacity-50 shrink-0" />}
                {c.verified && <BadgeCheck size={15} className="opacity-60 shrink-0" />}
              </h3>
              <span className="font-mono-label opacity-50">{c.city}, {c.state}{c.guide.meets ? ` · ${c.guide.meets}` : ""}</span>
            </div>
            <div className="col-span-6 md:col-span-3 hidden md:flex flex-wrap gap-1.5">
              {(c.guide.chips || []).slice(0, 2).map((ch) => (
                <span key={ch} className="font-mono-label border border-white/20 group-hover:border-ink/25 px-2 py-1 opacity-70">{ch}</span>
              ))}
            </div>
            <div className="col-span-8 md:col-span-3 flex items-center gap-3">
              <div className="h-1.5 flex-1 bg-white/10 group-hover:bg-ink/10">
                <div className="h-full bg-white group-hover:bg-ink transition-colors duration-300" style={{ width: `${c.guide.score}%` }} />
              </div>
              <span className="font-display text-2xl w-10 text-right">{c.guide.score}</span>
            </div>
            <div className="col-span-4 md:col-span-1 flex justify-end items-center gap-3 pr-9">
              <span className="font-mono-label opacity-50 hidden lg:block">{c.guide.grade?.toUpperCase()}</span>
              <ArrowUpRight size={18} className="opacity-40 group-hover:opacity-100" />
            </div>
          </Link>
          <button
            data-testid={`ranked-badge-btn-${c.slug}`}
            title="Get shareable rank badge"
            onClick={() => setBadgeClub(c)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-white opacity-40 hover:opacity-100 mix-blend-difference transition-opacity duration-150"
          >
            <ImageDown size={17} />
          </button>
          </div>
        ))}
      </section>

      <section className="light-section bg-paper text-ink px-5 lg:px-10 py-20" data-testid="ranked-methodology">
        <Reveal>
          <p className="font-mono-label text-ink/40">HOW EVERY CLUB WAS SCORED</p>
          <h2 className="font-display text-3xl lg:text-5xl mt-4">THE RUBRIC.</h2>
          <p className="text-ink/60 text-sm max-w-2xl mt-4">
            Weightings come from what runners themselves said mattered. Pace honesty is worth the most because "all paces welcome" being false is the most common complaint in running, full stop. A club cannot score well here on aesthetics.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10 mt-10">
          {RUBRIC.map(([w, name, desc]) => (
            <div key={name} className="bg-paper p-6">
              <p className="font-display text-4xl">{w}</p>
              <p className="font-mono-label text-ink/60 mt-2">{name.toUpperCase()}</p>
              <p className="text-sm text-ink/60 mt-3">{desc}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
          <div className="border border-ink/15 p-7" data-testid="ranked-green-flags">
            <p className="font-mono-label text-ink/50 mb-4">GREEN FLAGS</p>
            <ul className="space-y-2.5">
              {data.green_flags.map((f, i) => (
                <li key={i} className="flex gap-3 text-sm text-ink/75"><span className="font-mono-label text-ink/30">+</span>{f}</li>
              ))}
            </ul>
          </div>
          <div className="border border-ink/15 p-7" data-testid="ranked-red-flags">
            <p className="font-mono-label text-ink/50 mb-4">RED FLAGS</p>
            <ul className="space-y-2.5">
              {data.red_flags.map((f, i) => (
                <li key={i} className="flex gap-3 text-sm text-ink/75"><span className="font-mono-label text-ink/30">–</span>{f}</li>
              ))}
            </ul>
          </div>
        </div>
        <p className="font-mono-label text-ink/35 mt-10">
          FROM THE{" "}
          <a href={data.guide_url} target="_blank" rel="noopener noreferrer" className="link-underline text-ink/60">NINJATHLETE BEST RUN CLUBS IN AMERICA 2026 GUIDE</a>
          {" "}· HANDLES VERIFIED · UPDATED 2026
        </p>
      </section>

      <section className="px-5 lg:px-10 py-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-white/10">
        <div>
          <p className="font-display text-2xl lg:text-3xl">THINK YOUR CLUB BELONGS HERE?</p>
          <p className="text-white/50 text-sm mt-2">Submit it. Every club gets scored on the same rubric, no pay-to-play.</p>
        </div>
        <Link to="/submit" data-testid="ranked-submit-cta" className="btn-invert bg-white text-ink font-mono-label px-6 py-3.5 border border-white hover:bg-transparent hover:text-white shrink-0">
          SUBMIT A CLUB
        </Link>
      </section>
    </div>
  );
}
